import useEventListener from '@propertypal/shared/src/hooks/useEventListener';
import { AgentResult, AgentResults, Reason } from '@propertypal/shared/src/types/agents';
import puralise from '@propertypal/shared/src/utils/puralise';
import AgentBox from '@propertypal/web-ui/src/agents/AgentBox';
import { RowCenter } from '@propertypal/web-ui/src/layout';
import PageWrapper from '@propertypal/web-ui/src/layout/PageWrapper';
import Breadcrumbs from '@propertypal/web-ui/src/search/Breadcrumbs';
import PageNumbers from '@propertypal/web-ui/src/search/PageNumbers';
import { Heading, SmallHeading, Text, UnorderedList } from '@propertypal/web-ui/src/typography';
import React, { FunctionComponent, useState } from 'react';
import Loader from 'react-spinners/PulseLoader';
import { useTheme } from 'styled-components';
import AgentDetails from './AgentDetails';
import SearchAgents from './SearchAgents';

interface Props {
  results: AgentResults;
  onEmailClick: (agentResult: AgentResult) => void;
  loading: boolean;
  onSearch: (params: string) => void;
  reasons: Reason[];
}

const AgentsResults: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const [showTotals, setShowTotals] = useState<boolean>(false);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.code === 'KeyD' && e.ctrlKey) {
      setShowTotals(!showTotals);
    }
  };

  useEventListener('keydown', handleKeyDown);

  return (
    <>
      <SearchAgents searchParams={props.results.params} onSubmit={props.onSearch} />

      {props.loading && (
        <RowCenter mt={120} mb={120}>
          <Loader color={theme.backgroundLight} />
        </RowCenter>
      )}

      {!props.loading && props.results.agentDetails && (
        <>
          <Breadcrumbs crumbs={props.results.breadcrumbs} />

          <AgentDetails agentDetails={props.results.agentDetails} reasons={props.reasons} />
        </>
      )}

      {!props.loading && !props.results.agentDetails && (
        <>
          <Breadcrumbs crumbs={props.results.breadcrumbs} disableBack />

          <PageWrapper>
            <Heading mt={30}>{props.results.description.complete}</Heading>

            {props.results.results.length > 0 && (
              <>
                <Text mb={30}>
                  Page {props.results.page + 1} of {props.results.totalPages} (
                  <Text as="span" color={theme.primary} fontWeight="bold">
                    {props.results.totalResults}
                  </Text>{' '}
                  {puralise('agent', 'agents', props.results.totalResults)} found)
                </Text>

                {props.results.results.map((agent) => (
                  <AgentBox
                    key={agent.accountNumber}
                    agent={agent}
                    onEmailClick={props.onEmailClick}
                    showTotalProperties={showTotals}
                  />
                ))}

                <PageNumbers
                  currentUrl={props.results.url}
                  currentPage={props.results.page + 1}
                  totalPages={props.results.totalPages}
                />
              </>
            )}

            {props.results.results.length === 0 && (
              <>
                <Text mt={10} data-testid="estate-agents-no-results">
                  Sorry. We couldn&apos;t find any results for the search <b>{props.results.description.stem}</b>
                </Text>

                <SmallHeading mt={30} fontSize={20}>
                  TRY OUR TIPS FOR BETTER SEARCH RESULTS:
                </SmallHeading>

                <UnorderedList mb={60}>
                  <li>Double check your spelling</li>
                  <li>Enter an your postcode or the name of your town</li>
                  <li>Enter the name of an estate agent you wish to use</li>
                </UnorderedList>
              </>
            )}
          </PageWrapper>
        </>
      )}
    </>
  );
};

export default AgentsResults;
